import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import HomePage from './pages/HomePage';
import ReceivePage from './pages/ReceivePage';
import AssignPage from './pages/AssignPage';
import ReturnPage from './pages/ReturnPage';
import InventoryPage from './pages/InventoryPage'; // New unified inventory page
import ReportsPage from './pages/ReportsPage';
import AccountPage from './pages/AccountPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage'; // New job detail page
import SiteDetailPage from './pages/SiteDetailPage'; // Site detail page
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import SetupPage from './pages/SetupPage';
import ScanViewPage from './pages/ScanViewPage'; // Equipment scanner
import ProjectsPage from './pages/ProjectsPage'; // PM Demo page
import QuickActionsPage from './pages/QuickActionsPage'; // Unified scan hub (role-based)
import { ErrorProvider, ErrorBoundary } from './contexts/ErrorContext';
import { JobProvider } from './contexts/JobContext';
import { InventoryProvider } from './contexts/InventoryContext';
import { InventoryPageProvider } from './contexts/InventoryPageContext';
import { AuthProvider } from './contexts/AuthContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { SnackbarProvider } from 'notistack';
import DemoControlsPage from './pages/DemoControlsPage';

// Create a custom theme with BAVS colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#000000', // Black
    },
    secondary: {
      main: '#FF0000', // Red
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#333333',
    },
    error: {
      main: '#D32F2F',
    },
    success: {
      main: '#388E3C',
    },
    warning: {
      main: '#FF9800',
    },
    info: {
      main: '#2196F3',
    },
  },
  typography: {
    fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
    button: {
      textTransform: 'none',
    },
    // Set consistent font sizes based on JobsPage standards
    body1: {
      fontSize: '0.95rem', // Primary text content
    },
    body2: {
      fontSize: '0.9rem',  // Secondary text content
    },
    caption: {
      fontSize: '0.85rem', // Captions, timestamps, metadata
    },
    subtitle1: {
      fontSize: '1.1rem',  // Headers, equipment IDs
    },
    subtitle2: {
      fontSize: '1rem',    // Section headers
    },
    h6: {
      fontSize: '1.1rem',  // Dialog titles, card headers
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: '10px 20px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#000000',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 'bold',
          backgroundColor: '#f5f5f5',
        },
        body: {
          fontSize: '0.9rem', // Consistent table cell font size
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          height: 22,
        },
        sizeSmall: {
          fontSize: '0.65rem',
          height: 18,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.9rem',
        },
        secondary: {
          fontSize: '0.85rem',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          // Default icon color should be consistent unless explicitly overridden
          color: 'inherit',
        },
      },
    },
  },
});

function App() {
  return (
    <ErrorProvider>
      <ErrorBoundary fallback={
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Something went wrong!</h2>
          <p>The application has encountered an error. Please refresh the page or contact support.</p>
          <button onClick={() => window.location.reload()}>
            Refresh the page
          </button>
        </div>
      }>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
            <AuthProvider>
              <FeatureFlagProvider>
                {/* Move routes outside of InventoryProvider since it depends on JobProvider */}
                <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={
                  <JobProvider>
                    <InventoryProvider>
                      <InventoryPageProvider>
                        <Layout />
                      </InventoryPageProvider>
                    </InventoryProvider>
                  </JobProvider>
                }>
                  <Route index element={<RouteErrorBoundary><ProjectsPage /></RouteErrorBoundary>} />
                  <Route path="inventory" element={<RouteErrorBoundary><InventoryPage /></RouteErrorBoundary>} />
                  <Route path="receive" element={<RouteErrorBoundary><ReceivePage /></RouteErrorBoundary>} />
                  <Route path="quick-actions" element={<RouteErrorBoundary><QuickActionsPage /></RouteErrorBoundary>} />
                  <Route path="assign" element={<Navigate to="/inventory?tab=status" replace />} />
                  <Route path="checkout" element={<Navigate to="/quick-actions" replace />} />
                  <Route path="return" element={<Navigate to="/quick-actions" replace />} />
                  <Route path="install" element={<Navigate to="/quick-actions" replace />} />
                  <Route path="transfer" element={<Navigate to="/inventory?tab=status" replace />} />
                  <Route path="search" element={<Navigate to="/inventory" replace />} />
                  <Route path="summary" element={<Navigate to="/inventory?tab=summary" replace />} />
                  <Route path="scan" element={<Navigate to="/inventory?tab=status" replace />} />
                  <Route path="reports" element={<RouteErrorBoundary><ReportsPage /></RouteErrorBoundary>} />
                  <Route path="jobs" element={<RouteErrorBoundary><JobsPage /></RouteErrorBoundary>} />
                  <Route path="jobs/:jobId" element={<RouteErrorBoundary><JobDetailPage /></RouteErrorBoundary>} />
                  <Route path="jobs/:jobId/sites/:siteId" element={<RouteErrorBoundary><SiteDetailPage /></RouteErrorBoundary>} />
                  <Route path="account" element={<RouteErrorBoundary><AccountPage /></RouteErrorBoundary>} />
                  <Route path="admin" element={<RouteErrorBoundary><AdminPage /></RouteErrorBoundary>} />
                  <Route path="setup" element={<RouteErrorBoundary><SetupPage /></RouteErrorBoundary>} />
                  {/* PM Demo - Only visible to authorized users */}
                  <Route path="projects/*" element={<RouteErrorBoundary><ProjectsPage /></RouteErrorBoundary>} />
                  {/* Secret demo controls - not linked in navigation */}
                  <Route path="admin/demo-controls" element={<RouteErrorBoundary><DemoControlsPage /></RouteErrorBoundary>} />
                </Route>
              </Route>
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </FeatureFlagProvider>
            </AuthProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </ErrorProvider>
  );
}

export default App;